import { Quaternion, Vector3 } from 'three'
import { RigidBody } from './RigidBody'
import { CoMEstimator } from './CoMEstimator'
import { Rocket } from './Rocket'
import type { TuningConfig } from './config'
import type { PilotInput } from '../input/types'
import { FlatGround, type ContactWorld } from './collision'

export const G = 9.80665
const WORLD_UP = new Vector3(0, 1, 0)
/** Below this, thrust sizing stops tracking gravityScale so 0-g still flies. */
const MIN_THRUST_REFERENCE_GRAVITY = 0.25
/** Contact tolerance, m — resting contact has no penetration to detect. */
const CONTACT_SKIN = 0.02
/** ζ for the auto-level spring. See `applyAutoLevel`. */
const AUTO_LEVEL_DAMPING_RATIO = 0.22

export interface RocketDebug {
  /** World position of the controller. */
  origin: Vector3
  /** World unit vector the thrust pushes along (opposite the nozzle). */
  direction: Vector3
  /** Lever arm from the CoM, world frame. */
  lever: Vector3
  /** Newtons. */
  force: number
  /** Effective throttle after spool and tracking decay. */
  level: number
  tracked: boolean
}

export type ContactKind = 'none' | 'landed' | 'crash'

export interface PilotDebug {
  rockets: [RocketDebug, RocketDebug]
  /** World-frame torque applied this step, N·m. */
  netTorque: Vector3
  /** World-frame force applied this step, N. */
  netForce: Vector3
  /** Total thrust from both rockets, N. */
  thrustTotal: number
  /** Feet above the ground, m. */
  altitude: number
  /**
   * Horizontal distance from the CoM to the midpoint of the hands, tracking
   * space. This is the lever arm that turns pure-up thrust into pitch/roll —
   * the single most important number a new pilot is not being shown.
   */
  postureOffset: number
  speed: number
  tiltDegrees: number
}

/**
 * The pilot: one rigid body, two rockets, an estimated centre of mass
 * (DESIGN.md §2). Deliberately free of any Three.js scene dependency so the
 * headless step-response harness runs the exact same code as the headset.
 */
export class Pilot {
  readonly body = new RigidBody()
  readonly com: CoMEstimator
  readonly rockets: [Rocket, Rocket] = [new Rocket(), new Rocket()]

  /** CoM position in world space — this is `body.position`, mirrored for clarity. */
  readonly comWorld = new Vector3()

  readonly debug: PilotDebug = {
    rockets: [makeRocketDebug(), makeRocketDebug()],
    netTorque: new Vector3(),
    netForce: new Vector3(),
    thrustTotal: 0,
    altitude: 0,
    postureOffset: 0,
    speed: 0,
    tiltDegrees: 0,
  }

  grounded = false
  /** Set for exactly one step when a contact resolves. */
  lastContact: ContactKind = 'none'
  lastImpactSpeed = 0

  /** What the pilot collides with. Swap to a city without touching the physics. */
  world: ContactWorld

  constructor(public cfg: TuningConfig, world: ContactWorld = new FlatGround()) {
    this.world = world
    this.com = new CoMEstimator(cfg.nominalHeight, cfg.comHeightFraction)
    this.syncBodyProperties()
    this.body.position.set(0, this.com.height, 0)
    this.comWorld.copy(this.body.position)
  }

  /** Peak thrust of a single rocket, N. */
  get maxThrustPerRocket(): number {
    const g = Math.max(this.cfg.gravityScale, MIN_THRUST_REFERENCE_GRAVITY)
    return (this.cfg.twr * this.cfg.mass * G * g) / 2
  }

  /**
   * Height of the pilot's feet above the ground. Zero while standing — the
   * body rests with its capsule cap on the floor, so the CoM sits a radius
   * higher than the CoM height alone.
   */
  get altitude(): number {
    return this.body.position.y - this.com.height - this.cfg.bodyRadius
  }

  /** Combined throttle that holds a hover, for HUD and scripted tests. */
  get hoverThrottle(): number {
    const weight = this.cfg.mass * G * this.cfg.gravityScale
    return weight / (2 * this.maxThrustPerRocket)
  }

  syncBodyProperties(): void {
    this.body.setCylinderInertia(
      this.cfg.mass,
      this.com.standingHeight,
      this.cfg.bodyRadius,
      this.cfg.inertiaScale,
    )
  }

  step(input: PilotInput, dt: number): void {
    this.syncBodyProperties()
    this.lastContact = 'none'

    const cfg = this.cfg
    const q = this.body.orientation
    const comTracking = this.com.update(input.head, cfg, dt)
    const fMax = this.maxThrustPerRocket

    let thrustTotal = 0

    for (let i = 0; i < 2; i++) {
      const hand = input.hands[i]
      const rocket = this.rockets[i]
      const dbg = this.debug.rockets[i]

      const level = rocket.update(hand.throttle, hand.tracked, cfg, dt)

      // Exhaust leaves along the controller's aim −Z, so thrust on the body is
      // aim +Z, carried into world by the body's orientation.
      dbg.direction.set(0, 0, 1).applyQuaternion(hand.quaternion).applyQuaternion(q)
      // r = controller − CoM, in tracking space, rotated into world.
      dbg.lever.copy(hand.position).sub(comTracking).applyQuaternion(q)
      dbg.origin.copy(this.body.position).add(dbg.lever)
      dbg.force = level * fMax
      dbg.level = level
      dbg.tracked = hand.tracked

      if (dbg.force > 0) {
        _f.copy(dbg.direction).multiplyScalar(dbg.force)
        // The one line the whole game is built on: off-centre force is both
        // linear acceleration and torque.
        this.body.addForceAtOffset(_f, dbg.lever)
        thrustTotal += dbg.force
      }
    }

    // --- gravity ---
    _f.set(0, -this.cfg.mass * G * cfg.gravityScale, 0)
    this.body.addForce(_f)

    // --- quadratic air drag (also the hook for wingsuit lift later, §10) ---
    const speed = this.body.velocity.length()
    if (speed > 1e-4) {
      const dragMag = 0.5 * cfg.airDensity * cfg.dragCoefficient * cfg.dragArea * speed * speed
      _f.copy(this.body.velocity).multiplyScalar(-dragMag / speed)
      this.body.addForce(_f)
    }

    // --- angular damping: the "flailing limbs / RCS" assist ---
    //
    // τ = −k·I·ω, *not* −k·ω. A flat torque coefficient looks reasonable and is
    // a trap: yaw inertia is ~20x smaller than pitch/roll, so the same N·m per
    // rad/s bleeds yaw off ~20x faster than roll. Turning the knob up to calm
    // pitch then silently deletes yaw entirely.
    //
    // Scaling by the inertia tensor makes `angularDamping` a *rate* in 1/s:
    // spin decays with time constant 1/k on every axis, and the knob stays
    // orthogonal to `inertiaScale` instead of fighting it.
    if (cfg.angularDamping > 0) {
      _qInv.copy(q).invert()
      _t.copy(this.body.omega)
        .applyQuaternion(_qInv)
        .multiply(this.body.inertia)
        .multiplyScalar(-cfg.angularDamping)
        .applyQuaternion(q)
      this.body.addTorque(_t)
    }

    // --- optional auto-level ---
    if (cfg.autoLevelStrength > 0) this.applyAutoLevel(cfg.autoLevelStrength)

    this.debug.netForce.copy(this.body.force)
    this.debug.netTorque.copy(this.body.torque)
    this.debug.thrustTotal = thrustTotal

    this.body.integrate(dt)
    this.resolveGround(dt)

    this.comWorld.copy(this.body.position)
    _mid
      .copy(input.hands[0].position)
      .add(input.hands[1].position)
      .multiplyScalar(0.5)
      .sub(comTracking)
    this.debug.postureOffset = Math.hypot(_mid.x, _mid.z)
    this.debug.altitude = this.altitude
    this.debug.speed = this.body.velocity.length()
    this.debug.tiltDegrees = this.tiltDegrees()
  }

  /** Angle between the pilot's up axis and world up, degrees. */
  tiltDegrees(): number {
    _up.set(0, 1, 0).applyQuaternion(this.body.orientation)
    return Math.acos(Math.max(-1, Math.min(1, _up.dot(WORLD_UP)))) * (180 / Math.PI)
  }

  /**
   * Spring-damper toward upright. Deliberately leaves yaw completely free —
   * spinning about your own axis is a skill, not an error to correct.
   */
  private applyAutoLevel(strength: number): void {
    _up.set(0, 1, 0).applyQuaternion(this.body.orientation)
    _axis.copy(_up).cross(WORLD_UP)
    const sin = _axis.length()
    const cos = _up.dot(WORLD_UP)
    const angle = Math.atan2(sin, cos)

    // Scale the gain with pitch/roll inertia so the assist feels the same at
    // any inertiaScale — otherwise the two knobs fight each other.
    const kp = strength * 20 * this.body.inertia.x
    // Deliberately under-damped. The spring term is what cancels a standing
    // bias torque (hands held forward of the CoM); the *rate* term is what
    // destroys the feeling of rotational inertia. At the critically-damped
    // ζ≈0.8 this started at, the assist ate the spin coast — the two are
    // separable, and only the spring is load-bearing here. Residual rate
    // damping comes from `angularDamping`, which is axis-uniform by design.
    const kd = 2 * AUTO_LEVEL_DAMPING_RATIO * Math.sqrt(kp * this.body.inertia.x)

    if (sin > 1e-6) {
      _t.copy(_axis).multiplyScalar((angle / sin) * kp)
    } else {
      _t.set(0, 0, 0)
    }

    // Damp only the tilt rate, not yaw.
    _tiltRate.copy(this.body.omega).addScaledVector(WORLD_UP, -this.body.omega.dot(WORLD_UP))
    _t.addScaledVector(_tiltRate, -kd)
    this.body.addTorque(_t)
  }

  /**
   * Resolve the body against whatever the world is — a flat plane in Phase 1,
   * a city of AABBs in Phase 2 (§4, §6). The body is probed as three spheres
   * down its long axis; the deepest contact gets a proper impulse, so a bad
   * landing tumbles instead of sliding and a wall clips you rather than
   * swallowing you.
   */
  private resolveGround(dt: number): void {
    const comY = this.com.height
    const radius = this.cfg.bodyRadius
    const top = this.com.standingHeight - comY

    _feetLocal.set(0, -comY, 0).applyQuaternion(this.body.orientation)
    _midLocal.set(0, (top - comY) / 2, 0).applyQuaternion(this.body.orientation)
    _headLocal.set(0, top, 0).applyQuaternion(this.body.orientation)

    // Probe every sample and keep the deepest. Contacts are tested even at zero
    // penetration: a body resting at equilibrium has none, so a strictly
    // penetrating test reports "airborne" for a pilot standing perfectly still,
    // which silently breaks landing detection and the stand assist.
    let deepest: Vector3 | null = null
    let bestDepth = -Infinity
    let feetFirst = false

    for (const r of [_feetLocal, _midLocal, _headLocal]) {
      _probe.copy(this.body.position).add(r)
      const depth = this.world.probe(_probe, radius + CONTACT_SKIN, _normal)
      if (depth <= 0) continue
      // Compare true penetration, discounting the skin used to detect resting.
      const real = depth - CONTACT_SKIN
      if (real > bestDepth) {
        bestDepth = real
        deepest = r
        feetFirst = r === _feetLocal
        _contactNormal.copy(_normal)
      }
    }

    if (!deepest) {
      this.grounded = false
      return
    }

    if (bestDepth > 0) this.body.position.addScaledVector(_contactNormal, bestDepth)

    this.body.pointVelocity(deepest, _contactV)
    const normalSpeed = _contactV.dot(_contactNormal)
    this.lastImpactSpeed = Math.max(0, -normalSpeed)

    if (normalSpeed < 0) {
      const denom = this.body.inverseEffectiveMass(deepest, _contactNormal)
      const j = -normalSpeed / denom
      _f.copy(_contactNormal).multiplyScalar(j)
      this.body.applyImpulse(_f, deepest)

      // Friction, clamped by the Coulomb cone.
      this.body.pointVelocity(deepest, _contactV)
      _tangent
        .copy(_contactV)
        .addScaledVector(_contactNormal, -_contactV.dot(_contactNormal))
      const tangentSpeed = _tangent.length()
      if (tangentSpeed > 1e-4) {
        _tangent.multiplyScalar(1 / tangentSpeed)
        const jtMax = this.cfg.groundFriction * j
        const jt = Math.min(tangentSpeed / this.body.inverseEffectiveMass(deepest, _tangent), jtMax)
        _f.copy(_tangent).multiplyScalar(-jt)
        this.body.applyImpulse(_f, deepest)
      }

      // A landing needs feet down, roughly upright, onto something roughly
      // level — clipping a wall at walking pace is still a crash.
      const upright = this.tiltDegrees() < 40
      const levelSurface = _contactNormal.dot(WORLD_UP) > 0.7
      this.lastContact =
        this.lastImpactSpeed > this.cfg.crashSpeed || !feetFirst || !upright || !levelSurface
          ? 'crash'
          : 'landed'
    }

    this.grounded = _contactNormal.dot(WORLD_UP) > 0.5

    // Legs holding you up once you're actually standing (see cfg.standAssist).
    if (this.cfg.standAssist > 0 && this.lastContact !== 'crash') {
      const upright = this.tiltDegrees()
      if (upright < 60 && this.body.velocity.lengthSq() < 9) {
        _up.set(0, 1, 0).applyQuaternion(this.body.orientation)
        _axis.copy(_up).cross(WORLD_UP)
        const k = this.cfg.standAssist * 10 * this.body.inertia.x
        this.body.omega.addScaledVector(_axis, k * dt * 0.05)
        this.body.omega.multiplyScalar(Math.max(0, 1 - 6 * this.cfg.standAssist * dt))
      }
    }
  }

  /**
   * Pose for the XR rig: rotate the whole tracking space about the estimated
   * CoM, not the floor origin and not the headset (§2 — "rotating about your
   * belly instead of your feet", both correct and less nauseating).
   */
  getRigTransform(outPosition: Vector3, outQuaternion: Quaternion): void {
    outQuaternion.copy(this.body.orientation)
    outPosition
      .copy(this.com.value)
      .applyQuaternion(this.body.orientation)
      .multiplyScalar(-1)
      .add(this.body.position)
  }

  reset(position: Vector3, orientation?: Quaternion): void {
    this.body.reset(position, orientation)
    this.rockets[0].reset()
    this.rockets[1].reset()
    this.com.reset(this.cfg)
    this.grounded = false
    this.lastContact = 'none'
    this.lastImpactSpeed = 0
    this.comWorld.copy(position)
  }
}

function makeRocketDebug(): RocketDebug {
  return {
    origin: new Vector3(),
    direction: new Vector3(),
    lever: new Vector3(),
    force: 0,
    level: 0,
    tracked: false,
  }
}

const _f = new Vector3()
const _t = new Vector3()
const _up = new Vector3()
const _axis = new Vector3()
const _tiltRate = new Vector3()
const _feetLocal = new Vector3()
const _midLocal = new Vector3()
const _probe = new Vector3()
const _normal = new Vector3()
const _contactNormal = new Vector3()
const _mid = new Vector3()
const _headLocal = new Vector3()
const _contactV = new Vector3()
const _tangent = new Vector3()
const _qInv = new Quaternion()
